// ────────────────────────────────────────────────────────────────────────────
//  JoinInvite.tsx — Landing-Page für Einladungslinks
//
//  Owner teilt einen Link wie `https://custosoft.de/join/ABC123`. Dieser View:
//    1. Liest den Code aus der URL
//    2. Zeigt eine Org-Preview (über `/org/preview-code`)
//    3. Wenn nicht eingeloggt → Login-Button (Code wird ans `/login` als
//       `?next=/app/org?code=ABC123` weitergegeben → nach Login Auto-Beitritt)
//    4. Wenn eingeloggt → „Beitreten"-Button → `/org/join` mit Code → /app/org
//
//  Mobile-Best-Practice: Tappable App-Store-Button damit User auch direkt
//  in die iOS-App springen können (wo das Eingabe-Erlebnis schöner ist).
// ────────────────────────────────────────────────────────────────────────────

import { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { api, APIError } from '../lib/api'
import { useAuth } from '../lib/useAuth'

interface OrgPreview {
  orgId:         number
  orgName:       string
  ownerName:     string
  memberCount:   number
}

const APP_STORE_URL = 'https://apps.apple.com/app/custosoft/id6766051168'

export function JoinInvite() {
  const { code: rawCode } = useParams<{ code: string }>()
  const code = (rawCode ?? '').trim().toUpperCase()
  const { user, loading: authLoading } = useAuth()
  const navigate = useNavigate()

  const [preview, setPreview] = useState<OrgPreview | null>(null)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [joining, setJoining] = useState(false)
  const [joinError, setJoinError] = useState<string | null>(null)

  // Preview laden (auth-frei)
  useEffect(() => {
    if (!code || code.length < 4) {
      setPreviewError('Ungültiger Einladungscode.')
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const p = await api.get<OrgPreview>(`/org/preview-code?code=${encodeURIComponent(code)}`)
        if (!cancelled) setPreview(p)
      } catch (err) {
        if (cancelled) return
        if (err instanceof APIError && err.status === 404) {
          setPreviewError('Dieser Einladungscode ist abgelaufen oder ungültig.')
        } else {
          setPreviewError(err instanceof Error ? err.message : 'Vorschau konnte nicht geladen werden.')
        }
      }
    })()
    return () => { cancelled = true }
  }, [code])

  async function joinNow() {
    if (!user) {
      navigate(`/login?next=${encodeURIComponent(`/join/${code}`)}`)
      return
    }
    setJoining(true); setJoinError(null)
    try {
      await api.post('/org/join', { code })
      navigate('/app/org', { replace: true })
    } catch (err) {
      setJoinError(err instanceof Error ? err.message : 'Beitritt fehlgeschlagen.')
      setJoining(false)
    }
  }

  if (authLoading) return null

  return (
    <div className="min-h-full bg-app-bg flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md glass-strong rounded-3xl p-7 space-y-5">
        {/* Header */}
        <div className="flex flex-col items-center text-center gap-3">
          <div className="w-16 h-16 rounded-2xl grid place-items-center"
               style={{ background: 'linear-gradient(135deg,#8a44ee,#3a62ff)',
                        boxShadow: '0 12px 28px rgba(138,68,238,.42)' }}>
            <span className="text-2xl">🎟️</span>
          </div>
          <h1 className="text-2xl font-bold tracking-tight">
            Einladung zur Organisation
          </h1>
          <div className="text-xs uppercase tracking-widest text-app-text-mute">
            Code: <span className="font-mono font-semibold text-app-text">{code}</span>
          </div>
        </div>

        {/* Preview */}
        {previewError ? (
          <div className="rounded-xl px-4 py-3 text-sm text-center"
               style={{ background: 'rgba(255,87,87,.10)', color: '#ff5757',
                        border: '1px solid rgba(255,87,87,.30)' }}>
            {previewError}
          </div>
        ) : !preview ? (
          <div className="text-sm text-app-text-mute text-center py-3">
            Vorschau wird geladen…
          </div>
        ) : (
          <div className="rounded-xl p-4 space-y-1 glass">
            <div className="text-lg font-bold">{preview.orgName}</div>
            <div className="text-xs text-app-text-mute">
              Inhaber: {preview.ownerName} · {preview.memberCount} Mitglied
              {preview.memberCount === 1 ? '' : 'er'}
            </div>
          </div>
        )}

        {/* CTAs */}
        {preview && !previewError && (
          <div className="space-y-2">
            <button
              onClick={joinNow}
              disabled={joining}
              className="btn-primary w-full justify-center"
            >
              {joining
                ? 'Beitritt läuft…'
                : user ? 'Jetzt beitreten' : 'Einloggen & beitreten'}
            </button>
            {joinError && (
              <div className="text-xs text-center"
                   style={{ color: '#ff5757' }}>{joinError}</div>
            )}
            <a
              href={APP_STORE_URL}
              target="_blank" rel="noreferrer"
              className="block w-full text-center py-3 rounded-xl text-sm font-semibold bg-black text-white hover:bg-black/85 transition"
            >
               In der iOS-/Mac-App öffnen
            </a>
          </div>
        )}

        <div className="text-[10px] text-app-text-mute text-center pt-2">
          Beim Beitritt wird dein Profil mit dem Inhaber geteilt. Mehr in der{' '}
          <Link to="/app/profile" className="underline">Datenschutzerklärung</Link>.
        </div>
      </div>
    </div>
  )
}
